import React, { useEffect, useState } from "react";
import { useAuth } from "../../firebase/auth.tsx";
import {
  isEmailValid,
  getFirstNameErrors,
  getLastNameErrors,
} from "../helpers/helpers.tsx";
import {
  getHeadquarters,
  getHospitalsInHeadquarter,
} from "../helpers/requests.tsx";
import { useTranslation } from "react-i18next";

const CreateAccountModal = ({
  setShowCreateAccountModal,
  headsetId,
  headquarterId,
  hospitalId,
  userRole,
  adminView = false,
  headquarterView = false,
  groupView = false,
  handleCreateAccount,
}) => {
  const { registerUsers } = useAuth(); // Use registerUsers for bulk creation

  const [users, setUsers] = useState([
    {
      id: Date.now().toString(),
      email: "",
      firstName: "",
      lastName: "",
      role: userRole || "guest",
      headquarterId: headquarterId || "",
      hospitalId: hospitalId || "",
      hospitalName: "",
    },
  ]);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [serverErrors, setServerErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [hospitalsCache, setHospitalsCache] = useState<{ [key: string]: any[] }>(
    {}
  );

  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      if (adminView) {
        const headquarterList = await getHeadquarters();
        setHeadquarters(headquarterList);
      }
    };

    fetchData();
  }, [adminView]);

  const fetchHospitalsForHQ = async (hqId) => {
    if (!hqId || hospitalsCache[hqId]) return;
    try {
      const hospitalList = await getHospitalsInHeadquarter(hqId);
      setHospitalsCache((prev) => ({ ...prev, [hqId]: hospitalList }));
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    }
  };

  useEffect(() => {
    if (headquarterId) {
      fetchHospitalsForHQ(headquarterId);
    }
  }, [headquarterId]);

  const addUserRow = () => {
    setUsers([
      ...users,
      {
        id: Date.now().toString() + Math.random(),
        email: "",
        firstName: "",
        lastName: "",
        role: userRole || "guest",
        headquarterId: headquarterId || "",
        hospitalId: hospitalId || "",
        hospitalName: "",
      },
    ]);
  };

  const removeUserRow = (id) => {
    if (users.length > 1) {
      setUsers(users.filter((u) => u.id !== id));
    }
  };

  const updateUserField = (id, field, value) => {
    setUsers((prevUsers) =>
      prevUsers.map((u) => {
        if (u.id === id) {
          const updatedUser = { ...u, [field]: value };

          if (field === "role") {
            if (
              value !== "headquarterAdmin" &&
              value !== "user" &&
              value !== "headsetAdmin"
            ) {
              updatedUser.headquarterId = "";
              updatedUser.hospitalId = "";
            }
            if (headquarterId) updatedUser.headquarterId = headquarterId;
            if (hospitalId) updatedUser.hospitalId = hospitalId;
          }

          if (field === "headquarterId") {
            updatedUser.hospitalId = "";
            updatedUser.hospitalName = "";
            if (value) fetchHospitalsForHQ(value);
          }

          if (field === "hospitalId") {
            const hqId = updatedUser.headquarterId;
            if (hqId && hospitalsCache[hqId]) {
              const h = hospitalsCache[hqId].find((hos) => hos.id === value);
              updatedUser.hospitalName = h ? h.name : "";
            }
          }

          return updatedUser;
        }
        return u;
      })
    );
    setFormSubmitted(false);
    setErrorMessage("");
    setServerErrors({});
  };

  const validateUser = (user) => {
    if (!isEmailValid(user.email)) return false;
    if (getFirstNameErrors(user.firstName, t)) return false;
    if (getLastNameErrors(user.lastName, t)) return false;

    if (
      (user.role === "user" ||
        user.role === "headsetAdmin" ||
        user.role === "headquarterAdmin") &&
      !user.headquarterId
    ) {
      return false;
    }

    if (
      (user.role === "user" || user.role === "headsetAdmin") &&
      !user.hospitalId
    ) {
      return false;
    }
    return true;
  };

  const handleCreateAccountSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    setFormSubmitted(true);
    setErrorMessage("");
    setServerErrors({});

    for (const user of users) {
      if (!validateUser(user)) {
        return;
      }
    }

    setIsLoading(true);

    try {
      const payload = users.map((u) => ({
        email: u.email,
        password: "",
        firstName: u.firstName,
        lastName: u.lastName,
        headsetId: "",
        headquarterId: u.headquarterId,
        hospitalId: u.hospitalId,
        role: u.role,
      }));

      // Use the new method
      const result = await registerUsers(payload);

      if (result) {
        const successUsers = result.results || [];
        const errors = result.errors || [];

        if (errors.length > 0) {
            const newServerErrors: { [key: string]: string } = {};
            errors.forEach((err: any) => {
                newServerErrors[err.email] = err.error === "Email already in use" ? "Email_already_in_use" : "Invalid";
            });
            setServerErrors(newServerErrors);
            setErrorMessage("An_error_occurred_during_registration");
            setFormSubmitted(false);
        } else {
            if (successUsers.length > 0) {
                const createdUsers = successUsers.map(newUser => {
                    const original = users.find(u => u.email === newUser.email);
                    if (original) {
                        newUser.headquarterName = original.headquarterId 
                         ? headquarters.find(hq => hq.id === original.headquarterId)?.name 
                         : (newUser.headquarterName || "");
                        newUser.hospitalName = original.hospitalName || newUser.hospitalName;
                    }
                    return newUser;
                });
                handleCreateAccount(createdUsers);
            }
            setShowCreateAccountModal(false);
        }
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      setErrorMessage("An_error_occurred_during_registration");
      setFormSubmitted(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-modal">
      <div
        className="modal-content"
        style={{
          width: "90vw",
          maxWidth: "1200px",
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontSize: "1.3em",
            marginTop: "1vh",
            marginBottom: "2vh",
          }}
        >
          {t("Create_new_Users")}
        </div>

        <form
          onSubmit={handleCreateAccountSubmit}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              padding: "0 10px",
              marginBottom: "5px",
              fontWeight: "bold",
              color: "#404040",
            }}
          >
            <div style={{ flex: 2, marginRight: "10px" }}>{t("Email")}</div>
            <div style={{ flex: 1.5, marginRight: "10px" }}>
              {t("First_Name")}
            </div>
            <div style={{ flex: 1.5, marginRight: "10px" }}>
              {t("Last_Name")}
            </div>
            {(adminView || headquarterView || groupView) && (
              <div style={{ flex: 1.5, marginRight: "10px" }}>{t("Role")}</div>
            )}
            <div style={{ flex: 1.5, marginRight: "10px" }}>
              {t("Headquarter")}
            </div>
            <div style={{ flex: 1.5, marginRight: "10px" }}>
              {t("Hospital")}
            </div>
            <div style={{ width: "40px" }}></div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "10px 4px" }}>
            {users.map((user) => (
              <div
                key={user.id}
                style={{
                  display: "flex",
                  marginBottom: "10px",
                  alignItems: "start",
                }}
              >
                <div style={{ flex: 2, marginRight: "10px" }}>
                  <input
                    className="form-control"
                    placeholder="example@site.com"
                    value={user.email}
                    disabled={isLoading}
                    onChange={(e) =>
                      updateUserField(user.id, "email", e.target.value)
                    }
                    style={{
                      backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  />
                  {formSubmitted && !isEmailValid(user.email) && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t("Invalid")}
                    </div>
                  )}
                  {serverErrors[user.email] && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t(serverErrors[user.email])}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1.5, marginRight: "10px" }}>
                  <input
                    className="form-control"
                    placeholder="John"
                    value={user.firstName}
                    disabled={isLoading}
                    onChange={(e) =>
                      updateUserField(user.id, "firstName", e.target.value)
                    }
                    style={{
                      backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  />
                  {formSubmitted && getFirstNameErrors(user.firstName, t) && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t("Required")}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1.5, marginRight: "10px" }}>
                  <input
                    className="form-control"
                    placeholder="Smith"
                    value={user.lastName}
                    disabled={isLoading}
                    onChange={(e) =>
                      updateUserField(user.id, "lastName", e.target.value)
                    }
                    style={{
                      backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  />
                  {formSubmitted && getLastNameErrors(user.lastName, t) && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t("Required")}
                    </div>
                  )}
                </div>

                {(adminView || headquarterView || groupView) && (
                  <div style={{ flex: 1.5, marginRight: "10px" }}>
                    <select
                      className="form-control"
                      value={user.role}
                      disabled={isLoading}
                      onChange={(e) =>
                        updateUserField(user.id, "role", e.target.value)
                      }
                      style={{
                        backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                        borderRadius: "10px",
                        color: "#404040",
                        height: "35px",
                        paddingLeft: "10px",
                      }}
                    >
                      {adminView && <option value="guest">{t("Guest")}</option>}
                      {(adminView || headquarterView || groupView) && (
                        <>
                          <option value="user">{t("User")}</option>
                          <option value="headsetAdmin">
                            {t("Headset_Manager")}
                          </option>
                        </>
                      )}
                      {(adminView || headquarterView) && (
                        <option value="headquarterAdmin">
                          {t("Headquarter_Administrator")}
                        </option>
                      )}
                    </select>
                  </div>
                )}

                <div style={{ flex: 1.5, marginRight: "10px" }}>
                  {adminView &&
                  (user.role === "headquarterAdmin" ||
                    user.role === "user" ||
                    user.role === "headsetAdmin") ? (
                    <>
                      <select
                        className="form-control"
                        value={user.headquarterId}
                        disabled={isLoading}
                        onChange={(e) =>
                          updateUserField(
                            user.id,
                            "headquarterId",
                            e.target.value
                          )
                        }
                        style={{
                          backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                          borderRadius: "10px",
                          color: "#404040",
                          height: "35px",
                          paddingLeft: "10px",
                        }}
                      >
                        <option value=""></option>
                        {headquarters.map((headquarter: any) => (
                          <option key={headquarter.id} value={headquarter.id}>
                            {headquarter.name}
                          </option>
                        ))}
                      </select>
                      {formSubmitted && !user.headquarterId && (
                        <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                          {t("Required")}
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        color: "#888",
                        fontSize: "0.9em",
                      }}
                    >
                      {user.headquarterId ? "Selected" : "N/A"}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1.5, marginRight: "10px" }}>
                  {(adminView || headquarterView) &&
                  (user.role === "user" || user.role === "headsetAdmin") &&
                  user.headquarterId ? (
                    <>
                      <select
                        className="form-control"
                        value={user.hospitalId}
                        disabled={isLoading}
                        onChange={(e) =>
                          updateUserField(user.id, "hospitalId", e.target.value)
                        }
                        style={{
                          backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                          borderRadius: "10px",
                          color: "#404040",
                          height: "35px",
                          paddingLeft: "10px",
                        }}
                      >
                        <option value=""></option>
                        {(hospitalsCache[user.headquarterId] || []).map(
                          (hospital: any) => (
                            <option key={hospital.id} value={hospital.id}>
                              {hospital.name}
                            </option>
                          )
                        )}
                      </select>
                      {formSubmitted && !user.hospitalId && (
                        <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                          {t("Required")}
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        color: "#888",
                        fontSize: "0.9em",
                      }}
                    >
                      {user.hospitalId ? "Selected" : "N/A"}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    width: "40px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {users.length > 1 && (
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => removeUserRow(user.id)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: isLoading ? "#ccc" : "#A63939",
                        fontWeight: "bold",
                        cursor: isLoading ? "default" : "pointer",
                        fontSize: "1.2em",
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <button
              type="button"
              disabled={isLoading}
              onClick={addUserRow}
              className="btn btn-secondary"
              style={{
                backgroundColor: isLoading ? "#7ab09e" : "#39a686",
                borderColor: isLoading ? "#7ab09e" : "#39a686",
                color: "white",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5em",
                padding: 0,
                paddingBottom: "4px", // Visually center the +
                lineHeight: "1",
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              +
            </button>
          </div>

          {errorMessage && (
            <div
              style={{
                textAlign: "center",
                color: "#A63939",
                fontSize: "0.8em",
                margin: "1vh 0",
              }}
            >
              {t(errorMessage)}
            </div>
          )}

          <div className="button-container">
            <button
              type="button"
              className="buttonCancel btn btn-primary"
              disabled={isLoading}
              onClick={() => setShowCreateAccountModal(false)}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary buttonAcceptGreen"
              disabled={formSubmitted || isLoading}
            >
              {isLoading ? t("Loading...") : t("Create_accounts")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default CreateAccountModal;